// Shared labels and colors for task status / priority across the UI.

// Estado de SUMINISTRO (Insumos/Equipos): reemplaza al estado normal en esas
// tareas. `base` mapea a un estado estándar para reportes/semáforos.
export const SUPPLY = {
  solicitada: { label: "Solicitada", color: "bg-slate-200 text-slate-700", dot: "bg-slate-400", base: "not_started" },
  cotizado: { label: "Cotizado", color: "bg-amber-100 text-amber-700", dot: "bg-amber-500", base: "not_started" },
  aprobada: { label: "Aprobada", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500", base: "in_progress" },
  local: { label: "Local", color: "bg-violet-100 text-violet-700", dot: "bg-violet-500", base: "in_progress" },
  enviado: { label: "Enviado", color: "bg-sky-100 text-sky-700", dot: "bg-sky-500", base: "in_progress" },
  completada: { label: "Completada", color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500", base: "completed" },
};
export const SUPPLY_ORDER = ["solicitada", "cotizado", "aprobada", "local", "enviado", "completada"];
// Una tarea es "de suministro" si su stage está en SUPPLY.
export const isSupplyStage = (stage) => Boolean(stage && SUPPLY[stage]);

export const STATUS = {
  not_started: { label: "No iniciado", color: "bg-slate-200 text-slate-700", dot: "bg-slate-400" },
  in_progress: { label: "En progreso", color: "bg-blue-100 text-blue-700", dot: "bg-blue-500" },
  blocked: { label: "Bloqueado", color: "bg-rose-100 text-rose-700", dot: "bg-rose-500" },
  completed: { label: "Completado", color: "bg-emerald-100 text-emerald-700", dot: "bg-emerald-500" },
  postponed: { label: "Pospuesto", color: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
};

// Kanban column order
export const STATUS_ORDER = ["not_started", "in_progress", "blocked", "postponed", "completed"];

// Tipos de tarea (clave interna → etiqueta visible). Fuente única, usada por el
// modal de tarea y por la configuración de pestañas personalizadas (C1).
export const TASK_TYPES = [
  ["operativa", "Operativa"], ["estrategica", "Estratégica"], ["administrativa", "Administrativa"],
  ["incidente", "Incidencias"], ["proyecto", "Proyecto"], ["reunion", "Reunión"],
  ["inventario", "Equipos"], ["solicitud", "Insumos"], ["despacho", "Despacho"],
];

export const PRIORITY = {
  critical: { label: "Crítica", color: "bg-fuchsia-100 text-fuchsia-700", rank: 4 },
  high: { label: "Alta", color: "bg-rose-100 text-rose-700", rank: 3 },
  medium: { label: "Media", color: "bg-amber-100 text-amber-700", rank: 2 },
  low: { label: "Baja", color: "bg-slate-100 text-slate-600", rank: 1 },
};

// Gantt bar color by status
export const STATUS_BAR = {
  not_started: "#94a3b8",
  in_progress: "#3b82f6",
  blocked: "#f43f5e",
  completed: "#10b981",
  postponed: "#f59e0b",
};

// Risk of a follow-up task (time pressure + people + subtasks).
// `dot` es una clase CSS para renderizar un círculo de color (reemplaza los
// emojis 🔴🟠🟢 que teníamos antes; el usuario pidió UI 100% sin emojis).
export const RISK = {
  high: { label: "Riesgo alto", dot: "bg-rose-500", color: "bg-rose-100 text-rose-700", ring: "ring-2 ring-rose-300", bar: "#f43f5e" },
  medium: { label: "Riesgo medio", dot: "bg-amber-500", color: "bg-amber-100 text-amber-700", ring: "ring-1 ring-amber-200", bar: "#f59e0b" },
  low: { label: "Riesgo bajo", dot: "bg-emerald-500", color: "bg-emerald-100 text-emerald-700", ring: "", bar: "#10b981" },
};

export const TRAFFIC = {
  green: { dot: "bg-emerald-500", label: "En ritmo", color: "text-emerald-600" },
  yellow: { dot: "bg-amber-500", label: "Con atrasos", color: "text-amber-600" },
  red: { dot: "bg-rose-500", label: "Bloqueado", color: "text-rose-600" },
  gray: { dot: "bg-slate-300", label: "Sin actividad", color: "text-slate-400" },
};

export const ROLE_LABEL = {
  admin: "Administrador",
  manager: "Líder / Gerente",
  directivo: "Directivo",
  pmo: "PMO",
  supervisor: "Coordinador",
  member: "Miembro",
  country_manager: "Country Manager",
};

// Documentación Departamental
export const CONFIDENTIALITY = {
  normal: { label: "Normal", color: "bg-slate-100 text-slate-600", icon: "fileText" },
  restricted: { label: "Restringido", color: "bg-rose-100 text-rose-700", icon: "lock" },
};

export const DOC_ACTION = {
  upload: { label: "Subió", color: "text-emerald-600" },
  view: { label: "Vio", color: "text-slate-500" },
  download: { label: "Descargó", color: "text-blue-600" },
  share: { label: "Compartió", color: "text-violet-600" },
  update: { label: "Editó", color: "text-amber-600" },
  delete: { label: "Eliminó", color: "text-rose-600" },
};

// Ajustes → Permisos: capabilities matrix (role → what they can see/do).
// Cada etiqueta enumera TODO lo que la capacidad desbloquea (menús y submenús)
// para que el admin sepa exactamente qué está otorgando.
export const CAPS = [
  ["department_board", "Tablero de departamento y página de Proyectos"],
  ["dashboard", "Reportes: Dashboard ejecutivo · Estado actual de proyectos · Reporte de tareas"],
  ["metrics", "Reportes avanzados: Métricas · CFD · Gestión de tareas · Actividad por usuario · Estadísticas Zoho Desk (incl. Rendimiento diario)"],
  ["bitacora", "Ver Bitácora del sistema"],
  ["export_data", "Exportar datos (CSV/Excel): tareas, proyectos e informes — limitado al alcance del usuario"],
  ["send_reports", "Enviar informe ejecutivo / cierre de quincena por correo"],
  ["import_excel", "Importar tareas y proyectos (Excel, Zoho, OpenProject) y cargar CSV de Zoho Desk"],
  ["documents", "Ver Documentación Departamental"],
  ["manage_documents", "Subir / editar / borrar documentos"],
  ["manage_contacts", "Gestionar directorio de correos (incl. Country Managers)"],
  ["manage_departments", "Gestionar departamentos"],
  ["manage_users", "Gestionar usuarios (crear, editar, forzar cambio de clave, cerrar sesiones)"],
  ["manage_config", "Configuración del sistema (Ajustes, permisos, transferir trabajos)"],
  ["manage_security", "Seguridad: auditoría de accesos, IPs bloqueadas y respaldos"],
  // --- Módulo de Compras (P2P) ---
  ["compras", "Compras: ver y crear requisiciones (fase 1 del ciclo del gasto)"],
  ["compras_gestion", "Compras: cotizar, emitir órdenes de compra y registrar recepciones"],
  ["compras_directa", "Compras: solo compra directa — crear/gestionar proveedores, razones sociales y tiendas, y completar la factura de sus compras directas (sin cotización, OC ni el resto del ciclo)"],
  ["compras_aprobar", "Compras: aprobar la compra (Dirección / CFO)"],
];
export const PERM_ROLES = [["member", "Miembro"], ["supervisor", "Coordinador"], ["manager", "Líder/Gerente"], ["directivo", "Directivo"], ["admin", "Admin"]];
